import { useState, useEffect, useCallback } from 'react';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import { getCrossSellStats } from '@/lib/crossSell';
import { Badge } from '@/components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { TrendingUp, Receipt, ShoppingCart, Calendar, ChefHat, Check, X as XIcon, Eye } from 'lucide-react';
import { fetchMenuViewStats } from '@/lib/menuViewTracker';

const PERIOD_OPTIONS = [
  { label: 'Hoje', value: 'today' },
  { label: '7 dias', value: '7d' },
  { label: '30 dias', value: '30d' },
  { label: 'Tudo', value: 'all' },
];

function getStartDate(period) {
  const now = new Date();
  if (period === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  }
  if (period === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }
  if (period === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }
  return '2000-01-01T00:00:00.000Z';
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border-2 border-zinc-900 rounded-lg p-3 shadow-lg">
      <p className="text-xs font-bold text-zinc-500 mb-1">{label}</p>
      <p className="font-mono text-sm font-black text-zinc-900">
        {formatCurrency(payload[0].value)}
      </p>
    </div>
  );
}

export default function ReportsPage() {
  const [period, setPeriod] = useState('7d');
  const [sales, setSales] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [metrics, setMetrics] = useState({ revenue: 0, avgTicket: 0, totalSales: 0, totalItems: 0 });
  const [crossSell, setCrossSell] = useState({ shown: 0, accepted: 0, declined: 0, rate: 0 });
  // v8.12 — topo do funil: visualizações do cardápio + conversão real
  const [funnel, setFunnel] = useState({ views: 0, uniqueVisitors: 0, deliveryCount: 0, loaded: false });

  const loadData = useCallback(async () => {
    const startDate = getStartDate(period);
    const allSales = await db.salesHistory.toArray();
    // Fase 8: exclui registros em modo treinamento do financeiro real
    const filtered = allSales.filter((s) => s.closedAt >= startDate && !s.isTraining);

    // Include delivery orders as sales
    const allDelivery = await db.deliveryOrders.toArray();
    const filteredDelivery = allDelivery.filter((d) => d.status === 'delivered' && d.createdAt >= startDate && !d.isTraining);

    // Include paid commands (Fase 7+)
    const allCommands = await db.commands.toArray();
    const filteredCommands = allCommands.filter((c) => c.status === 'paid' && c.paidAt && c.paidAt >= startDate && !c.isTraining);

    // Combine all channels
    const combinedSales = [
      ...filtered.map((s) => ({ ...s, channel: 'local' })),
      ...filteredDelivery.map((d) => ({ ...d, closedAt: d.createdAt, channel: 'delivery' })),
      ...filteredCommands.map((c) => ({
        ...c,
        closedAt: c.paidAt,
        channel: 'comanda',
        itemCount: (c.items?.reduce((a, i) => a + i.quantity, 0) || 0),
      })),
    ];
    setSales(combinedSales);

    // Metrics
    const revenue = combinedSales.reduce((sum, s) => sum + (s.total || 0), 0);
    const totalSales = combinedSales.length;
    const avgTicket = totalSales > 0 ? revenue / totalSales : 0;
    const totalItems = combinedSales.reduce((sum, s) => sum + (s.itemCount || s.items?.reduce((a, i) => a + i.quantity, 0) || 0), 0);
    setMetrics({ revenue, avgTicket, totalSales, totalItems });

    // Daily revenue chart
    const dailyMap = {};
    combinedSales.forEach((s) => {
      const date = new Date(s.closedAt);
      const key = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      dailyMap[key] = (dailyMap[key] || 0) + (s.total || 0);
    });
    const dailyArr = Object.entries(dailyMap).map(([date, total]) => ({ date, total }));
    dailyArr.sort((a, b) => {
      const [da, ma] = a.date.split('/').map(Number);
      const [db2, mb] = b.date.split('/').map(Number);
      return ma - mb || da - db2;
    });
    setDailyData(dailyArr);

    // Top products
    const productMap = {};
    combinedSales.forEach((s) => {
      if (s.items) {
        s.items.forEach((item) => {
          const key = item.beverageName || item.name;
          if (!productMap[key]) {
            productMap[key] = { name: key, quantity: 0, revenue: 0 };
          }
          productMap[key].quantity += item.quantity;
          productMap[key].revenue += item.total || item.price * item.quantity || 0;
        });
      }
    });
    const topArr = Object.values(productMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
    setTopProducts(topArr);

    // Cross-sell stats (Fase 5)
    const cs = await getCrossSellStats(startDate);
    setCrossSell(cs);

    // v8.12 — Visualizações do cardápio (servidor) + conversão do funil delivery
    try {
      const stats = await fetchMenuViewStats(startDate);
      setFunnel({
        views: stats.views || 0,
        uniqueVisitors: stats.uniqueVisitors || 0,
        deliveryCount: filteredDelivery.length,
        loaded: true,
      });
    } catch {
      setFunnel((prev) => ({ ...prev, deliveryCount: filteredDelivery.length, loaded: false }));
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const barColors = ['#2563eb', '#3350F7', '#5570F9', '#7790FB', '#99B0FD', '#BBCFFE', '#DDDFFF', '#EEF0FF'];

  return (
    <div data-testid="reports-page" className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">
            Relatorios
          </h2>
          <p className="text-sm text-zinc-500 mt-1">Analise de vendas e faturamento</p>
        </div>
        <div data-testid="period-selector" className="flex gap-2">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              data-testid={`period-${opt.value}`}
              onClick={() => setPeriod(opt.value)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-150 active:translate-y-[1px] min-h-[40px] ${
                period === opt.value
                  ? 'bg-[#2563eb] text-white'
                  : 'bg-white text-zinc-600 border-2 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* v8.12 — Topo do Funil: Visualizações do Cardápio + Conversão Real */}
      <div data-testid="funnel-card" className="bg-zinc-900 border-2 border-zinc-800 rounded-xl p-6 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#2563eb]/15 blur-2xl pointer-events-none" />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">Funil do Cardápio Online</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div data-testid="funnel-views" className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Eye size={15} className="text-sky-400" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">👁️ Visualizações do Cardápio</p>
            </div>
            <p data-testid="funnel-views-value" className="font-mono text-3xl font-black text-white tracking-tighter">{funnel.views}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{funnel.uniqueVisitors} visitante{funnel.uniqueVisitors === 1 ? '' : 's'} único{funnel.uniqueVisitors === 1 ? '' : 's'}</p>
          </div>
          <div data-testid="funnel-orders" className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart size={15} className="text-emerald-400" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">Pedidos Delivery Concluídos</p>
            </div>
            <p data-testid="funnel-orders-value" className="font-mono text-3xl font-black text-white tracking-tighter">{funnel.deliveryCount}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">entregues no período</p>
          </div>
          <div data-testid="funnel-conversion" className="bg-gradient-to-br from-[#2563eb]/25 to-emerald-500/10 border border-[#2563eb]/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={15} className="text-[#7CA5FA]" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-300">🛒 Taxa de Conversão Real</p>
            </div>
            <p data-testid="funnel-conversion-value" className="font-mono text-3xl font-black text-white tracking-tighter">
              {funnel.views > 0 ? `${((funnel.deliveryCount / funnel.views) * 100).toFixed(1)}%` : '—'}
            </p>
            <p className="text-[11px] text-zinc-400 mt-0.5">pedidos concluídos ÷ visualizações</p>
          </div>
        </div>
        {!funnel.loaded && (
          <p data-testid="funnel-offline-note" className="text-[11px] text-amber-400/80 mt-3">Sem conexão com o servidor de métricas — visualizações indisponíveis no momento.</p>
        )}
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div data-testid="metric-revenue" className="bg-white border-2 border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-[#2563eb]" />
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">
              Faturamento
            </p>
          </div>
          <p className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">
            {formatCurrency(metrics.revenue)}
          </p>
        </div>
        <div data-testid="metric-avg-ticket" className="bg-white border-2 border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Receipt size={16} className="text-[#059669]" />
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">
              Ticket Medio
            </p>
          </div>
          <p className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">
            {formatCurrency(metrics.avgTicket)}
          </p>
        </div>
        <div data-testid="metric-total-sales" className="bg-white border-2 border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={16} className="text-[#F59E0B]" />
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">
              Vendas
            </p>
          </div>
          <p className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">
            {metrics.totalSales}
          </p>
        </div>
        <div data-testid="metric-total-items" className="bg-white border-2 border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart size={16} className="text-[#E11D48]" />
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">
              Itens Vendidos
            </p>
          </div>
          <p className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">
            {metrics.totalItems}
          </p>
        </div>
      </div>

      {/* Cross-Sell Conversion (Fase 5) */}
      <div data-testid="crosssell-conversion-card" className="bg-white border-2 border-zinc-200 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ChefHat size={18} className="text-[#2563eb]" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Conversão de Venda Cruzada (Sugestão do Chef)</p>
          </div>
          <Badge data-testid="crosssell-rate" className="bg-[#2563eb] text-white border-transparent font-mono text-sm">
            {crossSell.rate.toFixed(1)}% de aceitação
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart size={14} className="text-zinc-400" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">Sugestões exibidas</p>
            </div>
            <p data-testid="crosssell-shown" className="font-mono text-2xl font-black text-zinc-900 tracking-tighter">{crossSell.shown}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check size={14} className="text-emerald-600" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-700">Aceitas</p>
            </div>
            <p data-testid="crosssell-accepted" className="font-mono text-2xl font-black text-emerald-700 tracking-tighter">{crossSell.accepted}</p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <XIcon size={14} className="text-zinc-500" />
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500">Recusadas</p>
            </div>
            <p data-testid="crosssell-declined" className="font-mono text-2xl font-black text-zinc-600 tracking-tighter">{crossSell.declined}</p>
          </div>
        </div>

        {crossSell.shown > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
                <div className="bg-[#2563eb] h-full rounded-full transition-all duration-700" style={{ width: `${crossSell.rate}%` }} />
              </div>
              <span className="font-mono text-xs font-bold text-zinc-600 w-12 text-right">{crossSell.rate.toFixed(0)}%</span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              💡 Ajuste a tag <strong>Sugestão do Chef</strong> em /cardapio para testar diferentes itens e melhorar sua taxa.
            </p>
          </div>
        )}

        {crossSell.shown === 0 && (
          <p className="text-xs text-zinc-400 mt-4 text-center py-3">Nenhuma sugestão exibida no período.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Revenue Chart */}
        <div className="lg:col-span-2 bg-white border-2 border-zinc-200 rounded-xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-6">
            Faturamento Diario
          </p>
          {dailyData.length > 0 ? (
            <div data-testid="daily-revenue-chart" className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#71717A', fontFamily: 'IBM Plex Sans' }}
                    axisLine={{ stroke: '#E4E4E7' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#71717A', fontFamily: 'JetBrains Mono' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `R$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,32,245,0.05)' }} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={48}>
                    {dailyData.map((d, idx) => (
                      <Cell key={d.date ?? `cell-${idx}`} fill={idx === dailyData.length - 1 ? '#2563eb' : '#C7D2FE'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-zinc-300">
              <div className="text-center">
                <TrendingUp size={48} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Nenhuma venda no periodo</p>
              </div>
            </div>
          )}
        </div>

        {/* Top Products */}
        <div className="bg-white border-2 border-zinc-200 rounded-xl p-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-6">
            Ranking de Produtos
          </p>
          {topProducts.length > 0 ? (
            <div data-testid="top-products-list" className="space-y-3">
              {topProducts.map((product, idx) => {
                const maxRevenue = topProducts[0]?.revenue || 1;
                const barWidth = Math.max(10, (product.revenue / maxRevenue) * 100);
                return (
                  <div key={product.name} data-testid={`product-rank-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs font-bold text-zinc-400 w-5">
                          {idx + 1}.
                        </span>
                        <span className="text-sm font-semibold text-zinc-900 truncate">
                          {product.name}
                        </span>
                      </div>
                      <span className="font-mono text-sm font-bold text-zinc-900 ml-2 whitespace-nowrap">
                        {formatCurrency(product.revenue)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: barColors[idx] || '#2563eb',
                          }}
                        />
                      </div>
                      <span className="text-xs text-zinc-400 font-mono w-8 text-right">
                        {product.quantity}x
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-zinc-300">
              <div className="text-center">
                <ShoppingCart size={36} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Sem dados no periodo</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sales History Table */}
      {sales.length > 0 && (
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-4">
            Historico de Vendas
          </p>
          <div className="bg-white border-2 border-zinc-200 rounded-xl overflow-hidden">
            <table data-testid="sales-history-table" className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-200">
                  <th className="text-left p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Mesa</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Canal</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Operador</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Fechada em</th>
                  <th className="text-right p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Itens</th>
                  <th className="text-right p-3 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {sales
                  .sort((a, b) => (b.closedAt || '').localeCompare(a.closedAt || ''))
                  .slice(0, 50)
                  .map((sale) => (
                    <tr key={sale.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                      <td className="p-3 font-bold">
                        {sale.channel === 'delivery' ? `Delivery #${sale.id}` : `Mesa ${String(sale.tableNumber).padStart(2, '0')}`}
                      </td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${sale.channel === 'delivery' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {sale.channel === 'delivery' ? 'Delivery' : 'Local'}
                        </span>
                      </td>
                      <td className="p-3 text-zinc-600">{sale.operatorName || sale.customerName || '--'}</td>
                      <td className="p-3 text-zinc-500 font-mono text-xs">
                        {sale.closedAt ? new Date(sale.closedAt).toLocaleString('pt-BR') : '--'}
                      </td>
                      <td className="p-3 font-mono text-right">{sale.itemCount}</td>
                      <td className="p-3 font-mono font-bold text-right">{formatCurrency(sale.total)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
